import React, {Fragment, useState, useRef} from 'react';
import * as PropTypes from 'prop-types';
import I18n from 'i18n-js';
import {Formik, ErrorMessage} from 'formik';
import moment from 'moment-timezone/builds/moment-timezone-with-data';
import yup from '../../../lib/YupValidations';

import Button from '../../shared/Button';

import '../../../../../assets/svgs/fork-knife-disabled.svg';
import '../../../../../assets/svgs/fork-knife.svg'
import '../../../../../assets/svgs/wine-glass-disabled.svg';
import '../../../../../assets/svgs/wine-glass.svg';

import GoogleAutofillForm from '../../shared/Form/GoogleAutofillForm'
import {
  StyledButtonWrapper, StyledErrorImageWrapper,
  StyledErrorMessage, StyledErrorTitle,
  StyledField,
  StyledFloatLabel,
  StyledForm,
  StyledFragment,
} from '../../shared/Form/Form.styled';

import {
  GoogleAutofillWrapper,
  CreateRetailerFormMessage,
  FormHeader,
  StyledLabel,
  WaitMessage,
  WaitLabel,
  MessageContainer,
  RadioContainer,
  StyledSubmitButtonWrapper
} from './CreateRetailerForm.styled';
import Password from '../../shared/Form/Password';
import PhoneNumber from '../../shared/Form/PhoneNumber';

import countries from '../../../lib/CountryList';
import states from '../../../lib/StateList';
import ServerErrorMessage from '../../shared/Form/ServerErrorMessage';
import Radio from '../../shared/RadioGroup/Radio';
import RadioGroup from '../../shared/RadioGroup';


const formFields = [
  {
    component: 'input',
    id: 'beverage_retailer_name',
    label: I18n.t('address.establishmentName'),
    name: 'beverage_retailer_name',
    validation: yup.string().required(),
  },
  {
    component: 'input',
    id: 'address_line_1',
    label: I18n.t('address.addressLine1'),
    name: 'address_line_1',
    validation: yup.string().required(),
  },
  {
    component: 'input',
    id: 'address_line_2',
    label: I18n.t('address.addressLine2'),
    name: 'address_line_2',
    hideForWidget: true
  },
  {
    component: 'select',
    id: 'countryCode',
    label: I18n.t('address.country'),
    name: 'countryCode',
    validation: yup.string(),
    options: countries
  },
  {
    component: 'select',
    id: 'stateAddress',
    label: I18n.t('address.state'),
    name: 'stateAddress',
    validation: yup.string(),
    options: states,
    hideForWidget: true,
    isHidden: false
  },
  {
    component: 'input',
    id: 'regionAddress',
    label: I18n.t('address.state'),
    name: 'regionAddress',
    validation: yup.string(),
    hideForWidget: true,
    isHidden: true
  },
  {
    component: 'input',
    id: 'city',
    label: I18n.t('address.city'),
    name: 'city',
    validation: yup.string().required(),
    size: 2,
    isFirstInRow: true
  },
  {
    component: 'input',
    id: 'postal_code',
    label: I18n.t('address.zipCode'),
    name: 'postal_code',
    validation: yup.string().required(),
    size: 2,
    isLastInRow: true
  },
];

const additionalFormFields = [{
  component: 'input',
  id: 'firstName',
  label: I18n.t('shared.firstName'),
  name: 'firstName',
  validation: yup
    .string()
    .required()
    .trim()
    .max(40, 'First name is too long.'),
  size: 2,
  isFirstInRow: true
},
  {
    component: 'input',
    id: 'lastName',
    label: I18n.t('shared.lastName'),
    name: 'lastName',
    validation: yup
      .string()
      .required()
      .trim()
      .max(40, 'Last name is too long.'),
    size: 2,
    isLastInRow: true
  },
  {
    id: 'email',
    label: I18n.t('shared.workEmailAddress'),
    name: 'email',
    type: 'email',
    validation: yup
      .string()
      .email()
      .required()
      .max(40, 'Email is too long.'),
  },
  {
    id: 'phoneNumber',
    label: I18n.t('shared.mobilePhone'),
    name: 'phoneNumber',
    validation: yup.string().required(),
  },
  {
    id: 'password',
    label: I18n.t('shared.password'),
    name: 'password',
    type: 'password',
    customValidation: true,
    passwordHelper: true,
    validation: yup
      .string()
      .required('Password is required.')
      .min(6, 'Password is not strong.')
      .max(40, 'Password is too long.')
      .matches(/(?=.*[A-Z])/, 'Password is not strong.')
      .matches(/(?=.*[a-z])/, 'Password is not strong.')
      .trim(),
  }
];

const CreateRetailerForm = ({ account, createRetailer, isLoading, onrampType, redirectUrl,
                              skipRetailer, isWidget, createUser, /*emailError,*/ phoneError, errorMessage, userData
                            }) => {
  /*const [isBeverage, setIsBeverage] = useState(true);*/
  const [autosuggestionResults, setAutosuggestionResults] = useState({});
  const [accountType, setAccountType] = useState('');
  const createUserFormRef = useRef();
  const createRetailerFormRef = useRef();

  const retailerAccountType = onrampType || accountType;

  //Submit for embed widget
  const handleMultipleSubmit = async () => {
    const createRetailerAfter = true;
    const retailer_timezone = moment.tz.guess();
    const gettingStartedSource = window.location.hostname;
    //Form validation trigger
    if (createUserFormRef.current && createRetailerFormRef.current) {
      await createUserFormRef.current.setTouched({
        firstName: true,
        lastName: true,
        email: true,
        phoneNumber: true,
        password: true
      });
      await createRetailerFormRef.current.setTouched({
        beverage_retailer_name: true,
        address_line_1: true,
        address_line_2: true,
        countryCode: true,
        stateAddress: true,
        city: true,
        postal_code: true
      });
    }
    //Form error validation check
    if (!createUserFormRef.current.isValid || !createRetailerFormRef.current.isValid) {
      return;
    }

    //Create user data
    const createUserValues = createUserFormRef.current.values;
    //Create retailer data
    const createRetailerValues = createRetailerFormRef.current.values;
    createUser({
      ...createUserValues,
      account,
      skipRetailer,
      createRetailerAfter,
      createRetailerData: {
        ...createRetailerValues,
        retailer_timezone,
        gettingStartedSource,
        retailerAccountType,
        redirectUrl
      }
    })
  };

  const handleRetailerSubmit = values => {
    //Getting current user timezone
    const retailer_timezone = moment.tz.guess();
    const gettingStartedSource = window.location.hostname;
    createRetailer({...values, retailer_timezone, gettingStartedSource, retailerAccountType, redirectUrl, userData});
  };


  const validationSchema = yup.object().shape(
    formFields.reduce((obj, item) => {
      if (item.validation) {
        item.validation._label = item.label || I18n.t('validations.thisField')
        obj[item.name] = item.validation
      }
      return obj
    }, {}),
  );

  const additionalValidationSchema = yup.object().shape(
    additionalFormFields.reduce((obj, item) => {
      if (item.validation) {
        item.validation._label = item.label || I18n.t('validations.thisField')
        obj[item.name] = item.validation
      }
      return obj
    }, {}),
  );

  const initialValues = formFields.reduce((obj, item) => {
    obj[item.name] = autosuggestionResults[item.name] ? autosuggestionResults[item.name] : '';
    return obj
  }, {});

  const additionalInitialValues = additionalFormFields.reduce((obj, item) => {
    obj[item.name] = autosuggestionResults[item.name] ? autosuggestionResults[item.name] : '';
    return obj
  }, {});

  const validateField = field => {
    if(field.target.name === 'countryCode' && field.target.value !== 'US') {
      createRetailerFormRef.current.setFieldValue('regionAddress', '')
    }
  };

  return (
    <Fragment>
      {!isWidget && <><FormHeader>{I18n.t('retailerForm.createRetailerFormLabel')}</FormHeader>
        <CreateRetailerFormMessage>{I18n.t('retailerForm.createRetailerFormMessage')}</CreateRetailerFormMessage>
      </>}
      {((!onrampType || onrampType === 'test') && !isWidget) &&
      <RadioContainer>
        <RadioGroup
          name="beverageTypeRadio"
          onClickRadioButton={(selectedValue) => {
            setAccountType(selectedValue)
          }}
          selectedValue={accountType}
        >
          <Radio labelText={I18n.t('shared.beverage')} value=""/>
          <Radio labelText={I18n.t('shared.food')} value="inventory_template_food"/>
        </RadioGroup>
      </RadioContainer>
      }
      {isWidget &&
      <Formik initialValues={additionalInitialValues} innerRef={createUserFormRef}
              onSubmit={() => {
              }}
              validationSchema={additionalValidationSchema}>
        {({errors, touched, values}) => {
          return (
            <StyledForm>
              {additionalFormFields.map(field => (
                <Fragment key={field.label}>
                  <StyledFragment hideForWidget={field.hideForWidget} isFirstInRow={field.isFirstInRow}
                                  isHidden={field.isHidden}
                                  isLastInRow={field.isLastInRow}
                                  isWidget={isWidget} size={field.size}>
                    {field.component === 'input' && (
                      <>
                        <StyledField
                          component={field.component}
                          error={touched[field.name] && errors[field.name]}
                          id={field.id}
                          name={field.name}
                          type={field.type}
                          validate={field.validate}
                        />
                        <StyledFloatLabel
                          error={touched[field.name] && errors[field.name]}
                          hasvalue={values[field.name].length > 0}
                          htmlFor={field.id}
                        >
                          {field.label}
                        </StyledFloatLabel>
                      </>
                    )}
                    {field.type === 'password' && (
                      <Fragment>
                        <Password
                          error={touched[field.name] && errors[field.name]}
                          field={field}
                          hasvalue={values[field.name].length > 0}
                          helper={field.passwordHelper}
                        />
                      </Fragment>
                    )}
                    {field.id === 'email' && (
                      <>
                        <StyledField
                          component={field.component}
                          error={(touched[field.name] && errors[field.name])}
                          id={field.id}
                          name={field.name}
                          type={field.type}
                        />
                        <StyledFloatLabel
                          error={(touched[field.name] && errors[field.name])}
                          hasvalue={values[field.name].length > 0}
                          htmlFor={field.id}
                        >
                          {field.label}
                        </StyledFloatLabel>
                      </>
                    )}
                    {field.id === 'phoneNumber' && (
                      <Fragment>
                        <PhoneNumber
                          error={(touched[field.name] && errors[field.name])}
                          field={field}
                          phoneError={phoneError}
                        />
                      </Fragment>
                    )}
                    <ErrorMessage name={field.name}>
                      {msg => <StyledErrorMessage>
                        <StyledErrorImageWrapper>
                          <svg height='16' width='16'>
                            <use xlinkHref="#error"/>
                          </svg>
                        </StyledErrorImageWrapper>
                        <StyledErrorTitle>
                          {msg}
                        </StyledErrorTitle>
                      </StyledErrorMessage>}
                    </ErrorMessage>
                  </StyledFragment>
                </Fragment>
              ))}
            </StyledForm>
          )
        }}
      </Formik>
      }
      <GoogleAutofillWrapper>
        <GoogleAutofillForm setResults={setAutosuggestionResults}/>
      </GoogleAutofillWrapper>
      <Formik enableReinitialize={true} initialValues={initialValues} innerRef={createRetailerFormRef}
              onSubmit={values => handleRetailerSubmit(values)}
              validationSchema={validationSchema}>
        {({errors, touched, values}) => {
          // If country is US (default to US, hence the ''), show a state dropdown
          const stateField = formFields.find(field => field.name === 'stateAddress')
          const regionField = formFields.find(field => field.name === 'regionAddress')
          if (values.countryCode === '' || values.countryCode === 'US') {
            regionField.isHidden = true;
            stateField.isHidden = false;
          } else {
            regionField.isHidden = false;
            stateField.isHidden = true;
          }

          return (
            <StyledForm onChange={(target) =>
              validateField(target)}>
              {formFields.map(field => (
                <Fragment key={field.label}>
                  <StyledFragment hideForWidget={field.hideForWidget} isFirstInRow={field.isFirstInRow}
                                  isHidden={field.isHidden}
                                  isLastInRow={field.isLastInRow}
                                  isWidget={isWidget} size={field.size}>
                    {(field.component === 'input' && !(field.isHidden)) && (
                      <>
                        <StyledField
                          component={field.component}
                          error={touched[field.name] && errors[field.name]}
                          id={field.id}
                          name={field.name}
                          type={field.type}
                          validate={field.validate}
                        />
                        <StyledFloatLabel
                          error={touched[field.name] && errors[field.name]}
                          hasvalue={values[field.name].length > 0}
                          htmlFor={field.id}
                        >
                          {field.label}
                        </StyledFloatLabel>
                      </>
                    )}
                    {/* Country/State Dropdowns */}
                    {(field.component === 'select'  && !(field.isHidden)) && (
                      <>
                        <StyledLabel>{field.label}</StyledLabel>
                        <StyledField
                          component="select"
                          error={touched[field.name] && errors[field.name]}
                          id={field.id}
                          name={field.name}
                          validate={field.validate}
                        >
                          {field.options.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.name}
                            </option>
                          ))}
                        </StyledField>
                      </>
                    )}
                    <ErrorMessage name={field.name}>
                      {msg => <StyledErrorMessage>
                        <StyledErrorImageWrapper>
                          <svg height='16' width='16'>
                            <use xlinkHref="#error"/>
                          </svg>
                        </StyledErrorImageWrapper>
                        <StyledErrorTitle>
                          {msg}
                        </StyledErrorTitle>
                      </StyledErrorMessage>}
                    </ErrorMessage>
                  </StyledFragment>
                </Fragment>
              ))}
              {errorMessage && (<ServerErrorMessage text={errorMessage}/>)}
              {!isWidget && <StyledSubmitButtonWrapper>
                <Button
                  data-test-id={'createUserButton'}
                  disabled={isLoading}
                  isLoading={isLoading}
                  size={'full'}
                  type={'submit'}
                >
                  {I18n.t('shared.continue')}
                </Button>
              </StyledSubmitButtonWrapper>
              }
              {isLoading && (<MessageContainer>
                <WaitLabel>{I18n.t('loading.pleaseWaiteMessage')}</WaitLabel>
                <WaitMessage>{I18n.t('loading.createAccountMessage')}</WaitMessage>
              </MessageContainer>)}
            </StyledForm>
          )
        }}
      </Formik>
      {isWidget &&
      <StyledButtonWrapper>
        <Button data-test-id={'createUserButton'}
                disabled={isLoading}
                isLoading={isLoading}
                onClick={() => handleMultipleSubmit()}
                size={'full'}>{I18n.t('auth.getStarted')}</Button>
      </StyledButtonWrapper>
      }
    </Fragment>
  )
};

CreateRetailerForm.propTypes = {
  account: PropTypes.string,
  createRetailer: PropTypes.func,
  createUser: PropTypes.func,
  downloadableUrl: PropTypes.string,
  errorMessage: PropTypes.string,
  isLoading: PropTypes.bool,
  isWidget: PropTypes.bool,
  onrampType: PropTypes.string,
  phoneError: PropTypes.bool,
  redirectUrl: PropTypes.string,
  skipRetailer: PropTypes.bool,
  userData: PropTypes.any
};

CreateRetailerForm.defaultProps = {
  isWidget: false,
};

export default CreateRetailerForm;
